'use client';

import { Description, Field, Label } from '@headlessui/react';
import classnames from 'classnames';
import React, { forwardRef, useEffect, useState } from 'react';

import { useThemeClasses } from '@/lib/legacy-theme/theme-utils';

export interface NumberRange {
  min: number;
  max: number;
}

export interface RangeNumberPickerProps {
  label?: string;
  helperText?: string;
  error?: string;
  success?: string;
  variant?: 'default' | 'error' | 'success';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  value?: NumberRange;
  onChange?: (range: NumberRange) => void;
  disabled?: boolean;
  required?: boolean;
  min?: number;
  max?: number;
  step?: number;
  showValues?: boolean;
  valuePrefix?: string;
  valueSuffix?: string;
  formatValue?: (value: number) => string;
}

export const RangeNumberPicker = forwardRef<HTMLDivElement, RangeNumberPickerProps>(
  (
    {
      label,
      helperText,
      error,
      success,
      variant = 'default',
      size = 'md',
      fullWidth = false,
      value = { min: 0, max: 100 },
      onChange,
      disabled,
      required,
      min = 0,
      max = 100,
      step = 1,
      showValues = true,
      valuePrefix = '',
      valueSuffix = '',
      formatValue,
      ...props
    },
    ref,
  ) => {
    const [minValue, setMinValue] = useState(value.min);
    const [maxValue, setMaxValue] = useState(value.max);

    // Update internal state when value prop changes
    useEffect(() => {
      setMinValue(value.min);
      setMaxValue(value.max);
    }, [value.min, value.max]);

    // Base container classes
    const containerClasses = useThemeClasses(classnames('relative', fullWidth && 'w-full'));

    // Track container classes
    const trackContainerClasses = useThemeClasses('relative w-full h-2 bg-surface-hover rounded-theme-md');

    // Track fill classes (the selected range)
    const trackFillClasses = useThemeClasses(
      classnames('absolute h-full rounded-theme-md', variant === 'error' && 'bg-error', variant === 'success' && 'bg-success', variant === 'default' && 'bg-primary'),
    );

    // Thumb classes
    const thumbClasses = useThemeClasses(
      classnames(
        'absolute w-5 h-5 rounded-full border-2 border-white shadow-md cursor-pointer transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-offset-2',
        variant === 'error' && 'bg-error focus:ring-error',
        variant === 'success' && 'bg-success focus:ring-success',
        variant === 'default' && 'bg-primary focus:ring-primary',
        disabled && 'cursor-not-allowed opacity-50',
      ),
    );

    // Value display classes
    const valueDisplayClasses = useThemeClasses(classnames('text-theme-sm font-theme-medium text-text mt-2', disabled && 'opacity-50'));

    // Label classes
    const labelClasses = useThemeClasses(classnames('block font-theme-medium text-theme-sm text-text mb-theme-xs', disabled && 'opacity-50'));

    // Helper text classes
    const helperTextClasses = useThemeClasses(classnames('mt-theme-xs text-theme-xs', error ? 'text-error' : success ? 'text-success' : 'text-text-muted'));

    // Determine the actual variant based on error/success props
    const actualVariant = error ? 'error' : success ? 'success' : variant;

    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMin = Math.min(Number(e.target.value), maxValue - step);
      setMinValue(newMin);
      onChange?.({ min: newMin, max: maxValue });
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newMax = Math.max(Number(e.target.value), minValue + step);
      setMaxValue(newMax);
      onChange?.({ min: minValue, max: newMax });
    };

    const formatDisplayValue = (val: number): string => {
      if (formatValue) {
        return formatValue(val);
      }
      return `${valuePrefix}${val}${valueSuffix}`;
    };

    // Calculate positions for the track fill
    const minPercent = ((minValue - min) / (max - min)) * 100;
    const maxPercent = ((maxValue - min) / (max - min)) * 100;

    return (
      <div ref={ref} className={containerClasses}>
        <Field {...props}>
          {label && (
            <Label className={labelClasses}>
              {label}
              {required && <span className="text-error ml-1">*</span>}
            </Label>
          )}

          <div className="relative">
            {/* Track */}
            <div className={trackContainerClasses}>
              {/* Selected range fill */}
              <div
                className={trackFillClasses}
                style={{
                  left: `${minPercent}%`,
                  width: `${maxPercent - minPercent}%`,
                }}
              />

              {/* Min thumb */}
              <div className={thumbClasses} style={{ left: `calc(${minPercent}% - 10px)` }} />

              {/* Max thumb */}
              <div className={thumbClasses} style={{ left: `calc(${maxPercent}% - 10px)` }} />
            </div>

            {/* Hidden range inputs */}
            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={minValue}
              onChange={handleMinChange}
              disabled={disabled}
              className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
              style={{ zIndex: 1 }}
            />

            <input
              type="range"
              min={min}
              max={max}
              step={step}
              value={maxValue}
              onChange={handleMaxChange}
              disabled={disabled}
              className="absolute inset-0 w-full h-2 opacity-0 cursor-pointer"
              style={{ zIndex: 2 }}
            />

            {/* Value display */}
            {showValues && (
              <div className="flex justify-between items-center mt-2">
                <span className={valueDisplayClasses}>Min: {formatDisplayValue(minValue)}</span>
                <span className={valueDisplayClasses}>Max: {formatDisplayValue(maxValue)}</span>
              </div>
            )}
          </div>

          {(helperText || error || success) && <Description className={helperTextClasses}>{error || success || helperText}</Description>}
        </Field>
      </div>
    );
  },
);

RangeNumberPicker.displayName = 'RangeNumberPicker';

export default RangeNumberPicker;
